import React, { useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useHistory, useParams } from 'react-router-dom';
import { isSafari } from 'react-device-detect';
// Hooks
import { useAllArticles } from 'hooks/useAllArticles';
// Helpers
import { getArticlePersonName } from 'helpers/articles';
// Types
import {
  GetArticles_getArticles_entities,
  GetArticles_getArticles_entities_sourceInterviewee,
} from 'api/articles/types/GetArticles';
import { GetArticleDetails_getArticles_entities } from 'api/articles/types/GetArticleDetails';
import { ArticleStatus, ArticleTypes } from 'api/graphql-global-types';
// UI
import Table from 'ui/Table';
import { Tabs, Button, Modal, Spin } from 'antd';
import { errorNotification } from 'ui/Notification';
// Constants
import { commonTimeFormat } from 'constants/global';
import { ARTICLES } from 'constants/routes';
// Components
import CreateEditSponsoredArticles from './components/CreateEditSponsoredArticles/CreateEditSponsoredArticles';
import ArticleDetails from './components/ArticleDetails/ArticleDetails';
import Actions from './components/Actions/Actions';
// Styles
import styles from './Articles.module.scss';

const { TabPane } = Tabs;

const DEFAULT_NUMBER_ITEMS_PER_PAGE = 10;
const DEFAULT_CURRENT_PAGE = 1;

const TAB_TITLES: Record<ArticleTypes, string> = {
  [ArticleTypes.Sponsored]: 'Sponsored',
  [ArticleTypes.Generated]: 'Auto-generated',
};

const Articles: React.FC = () => {
  const history = useHistory();

  const { itemsPerPage, pageNumber, articlesType } = useParams<{
    articlesType: ArticleTypes | undefined;
    itemsPerPage: string | undefined;
    pageNumber: string | undefined;
  }>();

  const activeTabType =
    articlesType === ArticleTypes.Generated
      ? ArticleTypes.Generated
      : ArticleTypes.Sponsored;
  const pageSize = Number(itemsPerPage) || DEFAULT_NUMBER_ITEMS_PER_PAGE;
  const currentPage = Number(pageNumber) || DEFAULT_CURRENT_PAGE;

  const [detailsArticleId, setDetailsArticleId] = useState<string | null>(null);
  const [detailsModalData, setDetailsModalData] =
    useState<GetArticleDetails_getArticles_entities | null>(null);
  const openDetailsArticleId = useRef<string | null>(null);
  const [creationModalVisibility, setCreationModalVisibility] =
    useState<boolean>(false);
  const [updateModalVisibility, setUpdateModalVisibility] =
    useState<GetArticleDetails_getArticles_entities | null>(null);

  const { articles, loading, reload, loadArticleDetails } = useAllArticles();

  const articlesByType = useMemo(
    () => ({
      [ArticleTypes.Sponsored]: articles.filter(
        ({ type }) => type === ArticleTypes.Sponsored
      ),
      [ArticleTypes.Generated]: articles.filter(
        ({ type }) => type === ArticleTypes.Generated
      ),
    }),
    [articles]
  );

  const isFirstLoad = loading && !articles.length;

  const getTabTitle = (type: ArticleTypes) =>
    isFirstLoad
      ? TAB_TITLES[type]
      : `${TAB_TITLES[type]} (${articlesByType[type].length})`;

  const showDetailsModal = async (record: GetArticles_getArticles_entities) => {
    setDetailsArticleId(record.id);
    setDetailsModalData(null);

    openDetailsArticleId.current = record.id;

    try {
      const details = await loadArticleDetails(record.id);
      if (openDetailsArticleId.current === record.id) {
        setDetailsModalData(details);
      }
    } catch (error) {
      if (openDetailsArticleId.current === record.id) {
        hideDetailsModal();
        errorNotification((error as Error)?.message || 'Something went wrong');
      }
    }
  };

  function hideDetailsModal() {
    openDetailsArticleId.current = null;
    setDetailsArticleId(null);
    setDetailsModalData(null);
  }

  const handleCreateArticle = () => {
    setCreationModalVisibility(true);
  };

  const handleOpenEditArticleModal = async (
    record: GetArticles_getArticles_entities
  ) => {
    setUpdateModalVisibility(await loadArticleDetails(record.id));
  };

  const hideModal = () => {
    setCreationModalVisibility(false);
    setUpdateModalVisibility(null);
  };

  const columns = [
    {
      title: 'Article Id',
      dataIndex: 'id',
      align: 'left' as const,
      width: 130,
      render: function ActionsWrap(
        id: string,
        record: GetArticles_getArticles_entities
      ) {
        return (
          <span
            onKeyDown={() => showDetailsModal(record)}
            onClick={() => showDetailsModal(record)}
            className={styles.detailsModalLink}
            role="button"
            tabIndex={0}
          >
            {id}
          </span>
        );
      },
    },
    {
      title: 'Article Title',
      dataIndex: 'title',
      align: 'left' as const,
      width: 120,
    },
    {
      title: 'Profile Name',
      dataIndex: ['store', 'storeDetails', 'storeName'],
      align: 'left' as const,
      width: 120,
    },
    ...(activeTabType === ArticleTypes.Generated
      ? [
          {
            title: 'Interviewee',
            dataIndex: 'sourceInterviewee',
            align: 'left' as const,
            width: 120,
            render: (
              interviewee: GetArticles_getArticles_entities_sourceInterviewee | null
            ) => getArticlePersonName(interviewee),
          },
        ]
      : []),
    {
      title: 'Date',
      dataIndex: 'createdAt',
      align: 'left' as const,
      width: 120,
      render: (createdAt: string) => moment(createdAt).format(commonTimeFormat),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      align: 'center' as const,
      width: 100,
      render: (status: ArticleStatus) => {
        if (status === ArticleStatus.Active) {
          return <p className={styles.published}>Published</p>;
        } else {
          return <p className={styles.deactivated}>Deactivated</p>;
        }
      },
    },
    {
      title: 'Actions',
      dataIndex: 'id',
      fixed: 'right' as const,
      align: 'left' as const,
      width: 250,
      render: function ActionsWrap(
        id: string,
        record: GetArticles_getArticles_entities
      ) {
        return (
          <Actions
            record={record}
            onChanged={reload}
            onEdit={handleOpenEditArticleModal}
          />
        );
      },
    },
  ];

  const handleTabChange = (tabType: ArticleTypes | string) => {
    history.push(
      `${ARTICLES}/${tabType}/${DEFAULT_NUMBER_ITEMS_PER_PAGE}/${DEFAULT_CURRENT_PAGE}`
    );
  };

  const handleSetPagination = (page: number, size: number): void => {
    history.push(`${ARTICLES}/${activeTabType}/${size}/${page}`);
  };

  const isEditModalVisible = !!updateModalVisibility || creationModalVisibility;

  return (
    <>
      <Tabs activeKey={activeTabType} onChange={handleTabChange}>
        <TabPane
          tab={getTabTitle(ArticleTypes.Sponsored)}
          key={ArticleTypes.Sponsored}
        />
        <TabPane
          tab={getTabTitle(ArticleTypes.Generated)}
          key={ArticleTypes.Generated}
        />
      </Tabs>

      <div className={styles.articles}>
        {activeTabType === ArticleTypes.Sponsored && (
          <Button
            className={styles.createBtn}
            type="primary"
            size="large"
            onClick={handleCreateArticle}
          >
            Create new article
          </Button>
        )}

        <Table<GetArticles_getArticles_entities>
          key={activeTabType}
          columns={columns}
          data={articlesByType[activeTabType]}
          scroll={{ x: 300 }}
          loading={loading}
          total={articlesByType[activeTabType].length}
          pageSize={pageSize}
          currentPage={currentPage}
          setPageAndSize={handleSetPagination}
          scrollToTopOnPagination={!isSafari}
        />
      </div>

      {isEditModalVisible && (
        <CreateEditSponsoredArticles
          isVisible
          onClose={hideModal}
          onSaved={reload}
          record={updateModalVisibility} //if there is record, it is an edit modal-if there isn't, it's a creation modal
        />
      )}
      <Modal
        title="Article details"
        visible={!!detailsArticleId}
        footer={null}
        onCancel={hideDetailsModal}
        width="70vw"
      >
        {detailsModalData ? (
          <ArticleDetails record={detailsModalData} />
        ) : (
          <div className={styles.detailsLoading}>
            <Spin tip="Loading article…" />
          </div>
        )}
      </Modal>
    </>
  );
};

export default Articles;
